<template>
  <Funnel
    :data="finalChartData"
    :options="finalChartOptions"
    :plugins="plugins"
  />
</template>

<script lang="ts">
import {
  Chart as ChartJS,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  type Plugin,
  type ChartOptions,
  type ChartData as FinalChartData,
  type ChartType,
  type ChartDataset
} from 'chart.js'
import { createTypedChart } from 'vue-chartjs'
import { FunnelController, TrapezoidElement } from 'chartjs-chart-funnel'
import DataLabelsPlugin from 'chartjs-plugin-datalabels'
import { legendSpacing } from '~/assets/chart/plugins'
import exportedColors from '~/assets/style/_exportDefaultChartColors.module.scss'
import { formatMemberValue } from '~/services/analyticsFormat'
import { FormatContext, MemberFormat } from '~/types/analyticsFormat'
import type { ChartLabel } from '~/types/analytics'
import {
  DEFAULT_CHART_OPTIONS,
  TOOLTIP_PLUGIN_OPTIONS
} from '~/constants/chart'
import { getDeformedValuesForFunnelChart } from '~/services/chart'

const LEGEND_INCREASE_WIDTH = 16

const LegendSpacingPlugin = legendSpacing<'funnel'>(LEGEND_INCREASE_WIDTH)

ChartJS.register(Tooltip, Legend, TrapezoidElement, CategoryScale, LinearScale)

const Funnel = createTypedChart<ChartType, number[], string>(
  'funnel',
  FunnelController
)

type FunnelChartData = FinalChartData<'funnel', number[], string>

type FunnelDataset = ChartDataset<'funnel', number[]> & {
  valueOnChartLabelData: number[]
  rateOnChartLabelData: string[]
  rateOnSideLabelData: string[]
}

type ChartDataWithPartialDataset = {
  labels: ChartLabel[]
  datasets: {
    label: string
    data: (number | null)[]
  }[]
}

export default {
  name: 'ChartHorizontalFunnel',
  components: { Funnel },
  props: {
    chartData: {
      type: Object as PropType<ChartDataWithPartialDataset>,
      required: true
    }
  },
  setup() {
    return {
      plugins: [LegendSpacingPlugin, DataLabelsPlugin as Plugin<'funnel'>]
    }
  },
  computed: {
    finalChartOptions(): ChartOptions<'funnel'> {
      return {
        ...DEFAULT_CHART_OPTIONS,
        layout: {
          padding: {
            left: 0,
            right: 0,
            top: 32,
            bottom: 8
          }
        },
        indexAxis: 'x',
        scales: {},
        plugins: {
          legend: {
            display: true,
            position: 'right',
            labels: {
              font: {
                size: 13
              },
              usePointStyle: true,
              generateLabels: chart => {
                const { labels, datasets } = chart.data as FunnelChartData
                if (!labels || !datasets || !datasets[0]) {
                  return []
                }
                const backgroundColors = datasets[0].backgroundColor as string[]
                return labels.map((label, index) => ({
                  text: label,
                  hidden: false,
                  fillStyle: backgroundColors[index]!,
                  strokeStyle: backgroundColors[index]!,
                  pointStyle: 'rectRounded'
                }))
              }
            },
            onClick: () => {}
          },
          tooltip: {
            ...TOOLTIP_PLUGIN_OPTIONS,
            enabled: true,
            callbacks: {
              label: context => {
                return `${
                  (context.dataset as FunnelDataset).valueOnChartLabelData[
                    context.dataIndex
                  ]
                }`
              }
            }
          }
        }
      }
    },
    finalChartData(): FunnelChartData {
      const colors = Object.values(exportedColors)

      const valueOnChartLabelData = this.chartData.datasets
        .map(dataset => dataset.data[0])
        .filter(value => value) as number[]

      const deformedValues = getDeformedValuesForFunnelChart(
        this.chartData.datasets
      )

      const rateOnChartLabelData = this.chartData.datasets.map(dataset => {
        return formatMemberValue(
          (Number(dataset.data[0]) /
            Number(this.chartData.datasets[0]!.data[0])) *
            100,
          MemberFormat.PERCENT_0,
          FormatContext.CHART,
          false
        )
      })

      const rateOnSideLabelData = this.chartData.datasets.map(
        (dataset, index) => {
          return formatMemberValue(
            index === 0
              ? 1
              : (Number(dataset.data[0]) /
                  Number(this.chartData.datasets[index - 1]!.data[0])) *
                  100,
            MemberFormat.PERCENT_0,
            FormatContext.CHART,
            false
          )
        }
      )

      const labels = this.chartData.datasets.map(dataset => dataset.label)

      const dataset: FunnelDataset = {
        data: deformedValues,
        backgroundColor: colors,
        borderColor: colors,
        valueOnChartLabelData,
        rateOnChartLabelData,
        rateOnSideLabelData,
        datalabels: {
          labels: {
            onChartLabel: {
              color: 'white',
              font: {
                size: 13
              },
              anchor: 'start',
              offset: -20,
              formatter: (_, context) => {
                return `${
                  (context.dataset as FunnelDataset).valueOnChartLabelData[
                    context.dataIndex
                  ]
                }\n(${
                  (context.dataset as FunnelDataset).rateOnChartLabelData[
                    context.dataIndex
                  ]
                })`
              }
            },
            onSideLabel: {
              anchor: 'start',
              align: 285,
              offset: -190,
              backgroundColor: '#F2F5FA',
              borderRadius: 10,
              color: '#1E1E3C',
              font: {
                size: 14
              },
              display: context => {
                return context.dataIndex !== 0
              },

              formatter: (_, context) => {
                return `→ ${
                  (context.dataset as FunnelDataset).rateOnSideLabelData[
                    context.dataIndex
                  ]
                }`
              }
            }
          }
        }
      }

      return {
        labels,
        datasets: [dataset]
      }
    }
  }
}
</script>
